import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  LayoutDashboard, CalendarClock, Users, Receipt, Megaphone, Bot,
  Stethoscope, LogOut, ShieldCheck, Building2, Crown, Repeat, ClipboardList,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const NAV = {
  admin: [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["calendar", "Calendar", CalendarClock],
    ["patients", "Patients", Users],
    ["recall", "Recall", Repeat],
    ["forms", "Forms", ClipboardList],
    ["invoices", "Invoices & Claims", Receipt],
    ["ai", "AI Studio", Bot],
    ["announcements", "Announcements", Megaphone],
  ],
  practitioner: [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["calendar", "Calendar", CalendarClock],
    ["patients", "Patients", Users],
    ["recall", "Recall", Repeat],
    ["forms", "Forms", ClipboardList],
    ["invoices", "Invoices", Receipt],
    ["ai", "AI Studio", Bot],
    ["announcements", "Announcements", Megaphone],
  ],
  receptionist: [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["calendar", "Calendar", CalendarClock],
    ["patients", "Patients", Users],
    ["recall", "Recall", Repeat],
    ["forms", "Forms", ClipboardList],
    ["invoices", "Invoices", Receipt],
    ["announcements", "Announcements", Megaphone],
  ],
  billing: [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["invoices", "Invoices & Claims", Receipt],
  ],
};

const ROLE_LABELS = {
  admin: "Principal",
  practitioner: "Practitioner",
  receptionist: "Receptionist",
  billing: "Billing Manager",
};

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = NAV[user?.role] || NAV.receptionist;

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-border/60 bg-card lg:flex">
        <div className="flex items-center gap-2 px-6 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Stethoscope className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight leading-none">Practice OS™</div>
            <div className="text-[11px] text-muted-foreground">Elysiom Dental</div>
          </div>
        </div>
        <div className="mx-4 mb-4 flex items-center gap-2 rounded-xl bg-secondary px-3 py-2">
          <Building2 className="h-4 w-4 text-primary" />
          <div className="min-w-0">
            <div className="truncate text-xs font-semibold">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground">{ROLE_LABELS[user?.role]}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          {nav.map(([to, label, Icon]) => (
            <NavLink
              key={to}
              to={`/app/${to}`}
              data-testid={`nav-${to}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border/60 p-3">
          <div className="mb-2 flex items-center gap-2 px-2 text-[11px] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" /> POPIA audit active
          </div>
          <Button
            variant="ghost"
            data-testid="logout-btn"
            className="w-full justify-start gap-3 text-muted-foreground"
            onClick={() => { logout(); navigate("/login"); }}
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border/50 glass px-5 py-3">
          <div className="flex items-center gap-2 lg:hidden">
            <Stethoscope className="h-5 w-5 text-primary" />
            <span className="font-bold">Practice OS™</span>
          </div>
          <div className="hidden lg:block">
            <Badge variant="secondary" className="gap-1 rounded-full">
              <Crown className="h-3 w-3" /> Demo workspace
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-5 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
